import { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { sizedImg } from "@/lib/images";

/**
 * BeforeAfterSlider — draggable image comparison slider.
 * Props:
 *   before: string (URL)
 *   after:  string (URL)
 *   beforeLabel?: string
 *   afterLabel?:  string
 *   caption?: string
 */
export default function BeforeAfterSlider({
  before,
  after,
  beforeLabel = "PRIMA",
  afterLabel = "DOPO",
  caption,
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50); // percentage 0-100
  const [dragging, setDragging] = useState(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const p = Math.min(100, Math.max(0, (x / rect.width) * 100));
    setPos(p);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(clientX);
    };
    const up = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, setFromClientX]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
    else if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
  };

  return (
    <div className="w-full">
      <motion.div
        ref={containerRef}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        onMouseDown={(e) => { setDragging(true); setFromClientX(e.clientX); }}
        onTouchStart={(e) => { setDragging(true); setFromClientX(e.touches[0].clientX); }}
        data-testid="before-after-slider"
        className="relative aspect-[16/9] overflow-hidden border border-border bg-foreground select-none touch-none cursor-ew-resize group"
      >
        {/* AFTER image (base layer) */}
        <img
          src={sizedImg(after, 1600)}
          alt={afterLabel}
          data-testid="ba-after-img"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          loading="lazy"
          decoding="async"
          draggable={false}
        />

        {/* BEFORE image clipped on top */}
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={sizedImg(before, 1600)}
            alt={beforeLabel}
            data-testid="ba-before-img"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        </div>

        {/* Labels */}
        <span className="absolute top-4 left-4 md:top-6 md:left-6 font-mono text-[10px] md:text-xs uppercase tracking-widest bg-foreground/85 text-background px-3 py-1.5 pointer-events-none">
          {beforeLabel}
        </span>
        <span className="absolute top-4 right-4 md:top-6 md:right-6 font-mono text-[10px] md:text-xs uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1.5 pointer-events-none">
          {afterLabel}
        </span>

        {/* Divider line + handle */}
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white pointer-events-none"
          style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        >
          <button
            type="button"
            aria-label="Trascina per confrontare"
            data-testid="ba-handle"
            onKeyDown={onKey}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 grid place-items-center bg-white text-foreground border-2 border-white shadow-[0_10px_30px_rgba(0,0,0,0.35)] rounded-full cursor-ew-resize pointer-events-auto transition-transform group-hover:scale-110"
          >
            <MoveHorizontal className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      </motion.div>

      {caption && (
        <p className="mt-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {caption}
        </p>
      )}
    </div>
  );
}
